// Libraries
import { VictoryAxis, VictoryBar, VictoryChart, VictoryStack } from 'victory';
import { cii, stackBarChartAxis } from '@theme/victory';
// Types
import { StackBarChartProps } from './StackBarChart.types';
// Utils
import { colors } from '@theme/foundations';
import { formatNumberToDecimal } from '@helpers/index';

/**
 * A stack bar chart component to display FocContributionFactors as stacked bars.
 *
 * @component
 *
 * @param {StackBarChartProps} props - The props for the StackBarChart component.
 * @param {number[]} props.tickValues - An array of tick values for the dependent axis.
 * @param {FocContributionFactor[]} props.negativeContributionFactors - An array of FocContributionFactor objects in negative values.
 * @param {FocContributionFactor[]} props.positiveContributionFactors - An array of FocContributionFactor objects in positive values.
 * representing the data points for the stacked bars.
 * @returns {ReactElement} A React component displaying a stack bar chart.
 */
export const StackBarChart = ({
  tickValues,
  negativeContributionFactors,
  positiveContributionFactors,
}: StackBarChartProps) => (
  <VictoryChart theme={cii} height={40} padding={{ bottom: 30, left: 18, right: 18 }}>
    <VictoryAxis
      dependentAxis
      tickValues={tickValues}
      style={stackBarChartAxis}
      tickFormat={(value) => `${formatNumberToDecimal(value * 100, 1, false)}%`}
    />

    <VictoryStack
      horizontal
      colorScale={colors.stackBarChartColorScale}
      animate={{
        onLoad: { duration: 20 },
      }}
    >
      {negativeContributionFactors.map(({ factor, value }) => (
        <VictoryBar key={factor} barWidth={10} data={[{ y: value }]} />
      ))}
      {positiveContributionFactors.map(({ factor, value }) => (
        <VictoryBar key={factor} barWidth={10} data={[{ y: value }]} />
      ))}
    </VictoryStack>
  </VictoryChart>
);
